import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Injector,
  OnInit,
  Renderer2,
  ViewChild,
  effect,
} from '@angular/core';
import { ZonePageUrlProvider } from '@app/finxone-web-frontend/app/Modules/zones/zone-url-provider';
import { ConfigService } from '@app/finxone-web-frontend/app/lib/services/config-service/config-service.service';
import { CountriesCurrenciesService } from '@app/finxone-web-frontend/app/lib/services/countries-currencies-service/countries-currencies.service';
import { CtaButtonSignalService } from '@app/finxone-web-frontend/app/lib/services/cta-button-signal-service/cta-button-signal.service';
import { GoogleAnalyticsService } from '@app/finxone-web-frontend/app/lib/services/google-analytics/google-analytic.service';
import { WorkflowFormsService } from '@app/finxone-web-frontend/app/lib/services/workflow-forms/workflow-forms.service';
import {
  FormActionState,
  FormActionStateModel,
} from '@app/finxone-web-frontend/app/lib/state/form-submision.state';
import { ctaButtonFunctionsMap } from '@app/finxone-web-frontend/app/lib/utils/cta-button-actions/cta-button-actions.utils';
import { ctaButtonValidationMap } from '@app/finxone-web-frontend/app/lib/utils/cta-button-actions/cta-button-validations/cta-button-validation.utils';
import { ctaButtonShowHideMap } from '@app/finxone-web-frontend/app/lib/utils/cta-button-actions/cta-button-visibility-validations/cta-button-visibility.utils';
import {
  buildHandleBarModel,
  filterIfHandlebarNotRendered,
} from '@app/finxone-web-frontend/app/lib/utils/template/model-builder.utils';
import { AutoDestroy } from '@finxone-platform/shared/base-types';
import { IconCacheService } from '@finxone-platform/shared/services';
import {
  ButtonLoadingIndicatorType,
  ProviderClass,
  UiZoneWidgetAttributeConfig,
  baseCssConfigStyle,
  commonContentStyleList,
  commonTextContentStyleList,
  resolveButtonLoadingConfigFromCss,
} from '@finxone-platform/shared/sys-config-types';
import { Select, Store } from '@ngxs/store';
import Handlebars from 'handlebars';
import { Observable, Subject, map, takeUntil } from 'rxjs';
import { BaseComponent } from '../../base/base.component';

@Component({
  standalone: false,
  selector: 'finxone-web-frontend-cta-button-widget',
  templateUrl: './cta-button-widget.component.html',
  styleUrls: ['./cta-button-widget.component.scss'],
})
export class CtaButtonWidgetComponent extends BaseComponent implements OnInit, AfterViewInit {
  @ViewChild('buttonContent', { static: false })
  public buttonContent: ElementRef;
  @AutoDestroy destroy$: Subject<void> = new Subject<void>();
  public buttonLabel: string;
  public buttonLoadingText = '';
  public buttonIcon: string;
  public updatedBeneficiaryName: string;
  @Select(FormActionState.getFormActionState)
  formActionState!: Observable<FormActionStateModel>;
  public lefticon: string;
  public righticon: string;
  public leftIconStyles?: {
    width: string;
    height: string;
    color: string | undefined;
  };
  public rightIconStyles?: {
    width: string;
    height: string;
    color: string | undefined;
  };
  onboardingOrgId: string;
  isButtonActive = true;
  pageUrl = '';
  formActionDataWithId: any;
  public marginStyle: baseCssConfigStyle;
  isButtonHideShow = false;
  public globalStylingClass = '';
  public isAllowedGlobalStyling = false;
  public isLoadingState: boolean;
  public activationDelaySeconds?: number;
  public globalLoadingIndicatorType = ButtonLoadingIndicatorType.ANIMATION;
  public globalLoadingText = '';

  constructor(
    private renderer: Renderer2,
    private cdr: ChangeDetectorRef,
    public store: Store,
    private injector: Injector,
    private configService: ConfigService,
    public dataIn: ProviderClass<UiZoneWidgetAttributeConfig>,
    public zoneUrlprovider: ZonePageUrlProvider,
    private countriesCurrenciesService: CountriesCurrenciesService,
    private ctaButtonSignalService: CtaButtonSignalService,
    private iconCacheService: IconCacheService,
    private googleAnalyticService: GoogleAnalyticsService,
    private workflowFormsService: WorkflowFormsService,
  ) {
    super();
    this.formActionState
      .pipe(
        takeUntil(this.destroy$),
        map((response) => {
          this.formActionDataWithId = response;
        }),
      )
      .subscribe();
    this.pageUrl = this.zoneUrlprovider.pageUrlInput;
    const buttonType = this.dataIn.attri?.widgetProperties?.['buttonActionType'] ?? '';
    effect(
      () => (
        (this.isButtonActive =
          this.ctaButtonSignalService.buttonDisabled().find((button) => buttonType in button)?.[buttonType] ??
          true),
        (this.isButtonHideShow =
          this.ctaButtonSignalService.buttonHide().find((button) => buttonType in button)?.[buttonType] ??
          false),
        (this.isLoadingState =
          this.ctaButtonSignalService.buttonLoading().find((button) => buttonType in button)?.[buttonType] ??
          false)
      ),
    );
  }

  ngOnInit() {
    this.onLoadComponent();
    this.checkButtonStatus();
    this.checkButtonShowHideStatus();
  }

  ngAfterViewInit(): void {
    this.onSetDynamicStyle();
  }

  private onLoadComponent() {
    if (this.dataIn != undefined) {
      this.attri = this.dataIn.attri;
      this.getGlobalStyling(); // must run first: globalStylingClass/isAllowedGlobalStyling feed the global loading lookup below
      const labelOfButton = this.attri.widgetProperties['textContent'];
      if (this.attri.widgetProperties?.['navigationConfig']) {
        alert(
          'This action button contains deprecated property "navigationConfig". Please update the configuration to use "actionNavigationConfigurations" instead. Please review and update the configuration accordingly.',
        );
      }
      const template = Handlebars.compile(labelOfButton);
      const loadingText = this.attri.widgetProperties?.['loadingTextContent'] ?? '';
      const loadingTextTemplate = Handlebars.compile(loadingText);

      let combinedTemplates = `${labelOfButton ?? ''} ${loadingText}`;
      let globalLoadingTemplate: Handlebars.TemplateDelegate | undefined;

      if (this.isAllowedGlobalStyling) {
        const { indicatorType, rawLoadingText } = resolveButtonLoadingConfigFromCss(
          this.configService.globalCssVariables(),
          this.globalStylingClass,
        );
        this.globalLoadingIndicatorType = indicatorType;
        if (rawLoadingText) {
          globalLoadingTemplate = Handlebars.compile(rawLoadingText);
          combinedTemplates += ` ${rawLoadingText}`;
        }
      }

      buildHandleBarModel(
        this.store,
        combinedTemplates,
        this.countriesCurrenciesService,
        this.iconCacheService,
      )
        .pipe(takeUntil(this.destroy$))
        .subscribe((model) => {
          this.buttonLabel = template(model);
          this.buttonLoadingText = loadingTextTemplate(model);
          if (globalLoadingTemplate) {
            this.globalLoadingText = globalLoadingTemplate(model);
          }
        });
      this.buttonIcon = this.attri.widgetProperties?.icon as string;
      this.leftIconStyles = {
        width:
          (this.attri.css_config['lefticonSize'] ?? 20) + (this.attri.css_config['leftIconUnit'] ?? 'px'),
        height:
          (this.attri.css_config['lefticonSize'] ?? 20) + (this.attri.css_config['leftIconUnit'] ?? 'px'),
        color: this.attri.css_config['leftIconColor'],
      };
      this.rightIconStyles = {
        width:
          (this.attri.css_config['righticonSize'] ?? 20) + (this.attri.css_config['rightIconUnit'] ?? 'px'),
        height:
          (this.attri.css_config['righticonSize'] ?? 20) + (this.attri.css_config['rightIconUnit'] ?? 'px'),
        color: this.attri.css_config['iconColor'] ?? this.attri.css_config['rightIconColor'],
      };

      this.contentStyle = this.configService.getSpecificBaseCssStyle(this.attri, [
        ...commonContentStyleList,
        ...commonTextContentStyleList,
      ]);

      if (this.attri.widgetProperties?.lefticon || this.attri.widgetProperties?.righticon) {
        this.buttonIcon = '';
        this.lefticon = this.attri.widgetProperties?.lefticon as string;
        this.righticon = this.attri.widgetProperties?.righticon as string;
      }
      this.activationDelaySeconds = this.attri.widgetProperties?.delayButtonActivation
        ? this.attri.widgetProperties?.activationDelaySeconds
        : undefined;
    }
  }

  private getGlobalStyling() {
    if (
      this.dataIn.attri.widgetProperties?.['globalStylingClass'] &&
      this.dataIn.attri.widgetProperties?.['globalStyling']
    ) {
      this.globalStylingClass = this.dataIn.attri.widgetProperties?.['globalStylingClass'] ?? '';
      this.isAllowedGlobalStyling = this.dataIn.attri.widgetProperties?.['globalStyling'] ?? false;
    }
  }

  public doAction(): void {
    this.googleAnalyticService.sendEvent(
      this.attri.widgetProperties?.['buttonActionType'] ?? 'action-button',
    );
    const ctaActionType = this.attri.widgetProperties?.['buttonActionType'];
    if (ctaActionType) {
      const buttonAction = ctaButtonFunctionsMap.get(ctaActionType); // get the action by passing the button action type
      if (buttonAction) {
        // execute the required function/action
        const actionResult = buttonAction(this.injector, this.attri);
        if (actionResult instanceof Promise) {
          actionResult.catch((error: unknown) => {
            console.error('Button action failed', error);
          });
        }
      } else {
        console.error('Button action not defined, please check');
      }
    }
  }

  public onSetDynamicStyle(): void {
    if (this.dataIn?.attri && this.buttonContent?.nativeElement) {
      this.renderer.setStyle(
        this.buttonContent.nativeElement,
        '--form-submission-button-disabled-background-color',
        this.dataIn.attri.css_config?.disabledBgColor,
        2,
      );
      this.renderer.setStyle(
        this.buttonContent.nativeElement,
        '--form-submission-button-disabled-text-color',
        this.dataIn.attri.css_config?.disabledTextColor,
        2,
      );

      this.renderer.setStyle(
        this.buttonContent.nativeElement,
        '--form-submission-button-text-color',
        this.dataIn.attri.css_config?.color,
        2,
      );

      this.cdr.detectChanges(); // Manually trigger change detection
    }
  }

  public checkButtonShowHideStatus(): void {
    const ctaActionType = this.attri.widgetProperties?.['buttonActionType'];
    if (ctaActionType) {
      const buttonAction = ctaButtonShowHideMap.get(ctaActionType);
      if (buttonAction) {
        buttonAction(
          this.store,
          this.ctaButtonSignalService,
          this.attri.widgetProperties as any,
          this.configService,
        );
      }
    }
  }
  public checkButtonStatus(): void {
    const ctaActionType = this.attri.widgetProperties?.['buttonActionType'];
    if (ctaActionType) {
      const buttonAction = ctaButtonValidationMap.get(ctaActionType);
      if (buttonAction) {
        buttonAction(
          this.store,
          this.ctaButtonSignalService,
          this.attri.widgetProperties as any,
          this.configService,
          this.workflowFormsService,
        );
      }
    }
  }
}
